/** @import { Signal } from "@preact/signals"; */
import { batch, signal } from "@preact/signals";

/**
 * @template Data
 * @typedef {object} QueryState
 * @property {boolean} initialized
 * @property {boolean} loading
 * @property {number} generation
 * @property {Data} [data]
 * @property {Error} [error]
 * @property {Promise<Data>} [promise] The in-flight request, if any, used to deduplicate concurrent
 *   fetches
 */

/**
 * An in-memory cache of query state. We don't keep track of which queries are mounted, so each
 * value is stored inside a `WeakRef` to allow it to be garbage collectd.
 *
 * @type {Map<string, WeakRef<Signal<QueryState<any>>>>}
 */
const cache = new Map();

export function clearCache() {
	batch(() => {
		for (const [key, value] of cache.entries()) {
			const state = value.deref();
			if (!state) {
				cache.delete(key);
				continue;
			}
			state.value = { ...Query.DEFAULT_STATE, generation: state.peek().generation + 1 };
		}
	});
}

/**
 * @template {any[]} const Key
 * @template {any} Data
 */
export default class Query {
	#props;
	#key;
	#keyJson;
	#query;
	#options;
	#fetchedGeneration = -1;

	/** @type {QueryState<any>} */
	static DEFAULT_STATE = {
		initialized: false,
		loading: false,
		generation: 0,
	};

	/** @type {Signal<Signal<QueryState<Data>>>} */
	#state = signal(signal({ ...Query.DEFAULT_STATE }));

	/**
	 * @param {() => { key: Key; enabled?: boolean; keepPreviousData?: boolean }} props A function
	 *   returning a JSON-serializable key.
	 * @param {(key: Key) => Promise<Data>} query A function that takes the query key and returns a
	 *   promise with the query data.
	 */
	constructor(props, query) {
		this.#props = props;
		this.#query = query;

		const { key, ...options } = props();
		this.#options = { enabled: true, keepPreviousData: false, ...options };
		this.#key = key;
		this.#keyJson = JSON.stringify(key);

		this.#init();
	}

	/**
	 * Re-evaluate `get` to refresh the options and, if the key changed, re-bind to the new key's
	 * cache entry and fetch it.
	 */
	#reconcile() {
		const { key, ...options } = this.#props();
		this.#options = { enabled: true, keepPreviousData: false, ...options };

		const json = JSON.stringify(key);
		if (json === this.#keyJson) return;

		this.#key = key;
		this.#keyJson = json;
		this.#init();
		this.#fetch();
	}

	#init() {
		this.#fetchedGeneration = -1;

		let state = cache.get(this.#keyJson)?.deref();
		if (!state) {
			state = signal({ ...Query.DEFAULT_STATE });
			if (this.#options.keepPreviousData) state.value.data = this.#state.peek().peek().data;

			cache.set(this.#keyJson, new WeakRef(state));
		}

		this.#state.value = state;
	}

	#fetch() {
		if (!this.#options.enabled) return;
		// the error is captured in state.error; swallow the rejection so the
		// lazy-fetch entry point doesn't produce unhandled rejections
		this.fetch().catch(e => {
			if (import.meta.env?.PRODUCTION) return;
			console.error(e);
		});
	}

	/**
	 * Fetches data for this query, respecting the cache and deduplicating concurrent requests. This
	 * will fetch the data even if the query is disabled or the data is fresh.
	 *
	 * @returns {Promise<Data>} A promise that resolves with the query data or rejects with an error
	 */
	async fetch() {
		this.#reconcile();

		const cached = this.#state.peek();
		const initial = cached.peek();

		// capture the generation this request belongs to. if an invalidation bumps it
		// while the request is in flight, the result is stale and must not be committed.
		const generation = initial.generation;
		this.#fetchedGeneration = generation;

		// if this query already has a request in flight,
		// return its promise so concurrent callers share the same result
		if (initial.loading && initial.promise) return initial.promise;

		// store the shared promise on the state for deduplication
		/** @type {PromiseWithResolvers<Data>} */
		const { promise, resolve, reject } = Promise.withResolvers();
		cached.value = { ...initial, loading: true, promise };

		try {
			// fetch the query data
			const data = await this.#query(this.#key);

			// if the cache entry was invalidated while this request was in flight, don't
			// commit the stale result — but still settle waiters below with the data
			if (generation === cached.peek().generation) {
				// save the successful query and data, omitting any error or stale promise
				const { error: _e, promise: _p, ...state } = cached.peek();
				cached.value = { ...state, data, loading: false, initialized: true };
			}

			resolve(data);
		} catch (error) {
			// likewise, don't commit a stale error — but still settle waiters below with it
			if (generation === cached.peek().generation) {
				// save the unsuccessful query and error, omitting any stale promise
				const { promise: _p, ...state } = cached.peek();
				cached.value = { ...state, error: /** @type {Error} */ (error), loading: false };
			}

			reject(error);
		}

		return promise;
	}

	get data() {
		this.#reconcile();

		// only a fetch marks the generation as fetched, so a query that is disabled
		// when the generation changes still fetches once it becomes enabled
		const { generation } = this.#state.value.value;
		if (this.#fetchedGeneration !== generation) this.#fetch();

		return this.#state.value.value.data;
	}

	get loading() {
		this.#reconcile();
		return this.#state.value.value.loading;
	}

	get initialized() {
		this.#reconcile();
		return this.#state.value.value.initialized;
	}

	get error() {
		this.#reconcile();
		return this.#state.value.value.error;
	}

	/** @param {...any[]} keys */
	static invalidate(...keys) {
		const matches = keys.length ? keys.map(key => key.map(part => JSON.stringify(part))) : [[]];

		// batching ensures that all mounted queries (if any)
		// receive their reset states simultaneously
		batch(() => {
			for (const [cacheKey, value] of cache.entries()) {
				const state = value.deref();

				// if the query state has been garbage collected, delete the key from the map
				if (!state) {
					cache.delete(cacheKey);
					continue;
				}

				/** @type {any[]} */
				const parts = JSON.parse(cacheKey);
				const matched = matches.some(match =>
					match.every((part, i) => part === JSON.stringify(parts[i])),
				);
				if (!matched) continue;

				const { data, initialized, generation } = state.peek();
				state.value = { ...Query.DEFAULT_STATE, data, initialized, generation: generation + 1 };
			}
		});
	}
}
